import { YesOrNo } from "./YesOrNo";

/** Payload / UI field keys for Spa → Services. */
export const SPA_SERVICES_FIELD_SERVICE_GROUP = "Service Group";
export const SPA_SERVICES_FIELD_SERVICE_GROUP_NAME = "Service Group Name";
export const SPA_SERVICES_FIELD_SERVICE_GROUP_CODE = "Service Group Code";
export const SPA_SERVICES_FIELD_SERVICE_CODE = "Service Code";
export const SPA_SERVICES_FIELD_SERVICE_DESCRIPTION = "Service Description";
export const SPA_SERVICES_FIELD_SERVICE_POINTS = "Service Points";
export const SPA_SERVICES_FIELD_EFFECTIVE_FROM_DATE = "Effective From Date";
export const SPA_SERVICES_FIELD_EFFECTIVE_TO_DATE = "Effective To Date";
export const SPA_SERVICES_FIELD_SERVICE_PRICE = "Service Price";
export const SPA_SERVICES_FIELD_SERVICE_TIME = "Service Time";
export const SPA_SERVICES_FIELD_SETUP_TIME = "Setup Time";
export const SPA_SERVICES_FIELD_BREAKDOWN_TIME = "Breakdown Time";
export const SPA_SERVICES_FIELD_MINIMUM_AGE = "Minimum Age";
export const SPA_SERVICES_FIELD_MINIMUM_GUESTS = "Minimum Guests";
export const SPA_SERVICES_FIELD_MAXIMUM_GUESTS = "Maximum Guests";
export const SPA_SERVICES_FIELD_MINIMUM_STAFF = "Minimum Staff";
export const SPA_SERVICES_FIELD_MAXIMUM_STAFF = "Maximum Staff";
export const SPA_SERVICES_FIELD_OFFSITE = "Offsite";
export const SPA_SERVICES_FIELD_AUTO_GRATUITY = "Auto Gratuity";
export const SPA_SERVICES_FIELD_AUTO_GRATUITY_PCT = "Auto Gratuity %";
export const SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE = "Auto Service Charge";
export const SPA_SERVICES_FIELD_AUTO_SERVICE_CHARGE_PCT =
  "Auto Service Charge %";
export const SPA_SERVICES_FIELD_COMMISSIONABLE = "Commissionable";
export const SPA_SERVICES_FIELD_AVAILABLE_ON_WEB = "Available on Web";
export const SPA_SERVICES_FIELD_AVAILABLE_SUN = "Available Sun";
export const SPA_SERVICES_FIELD_AVAILABLE_MON = "Available Mon";
export const SPA_SERVICES_FIELD_AVAILABLE_TUE = "Available Tue";
export const SPA_SERVICES_FIELD_AVAILABLE_WED = "Available Wed";
export const SPA_SERVICES_FIELD_AVAILABLE_THU = "Available Thu";
export const SPA_SERVICES_FIELD_AVAILABLE_FRI = "Available Fri";
export const SPA_SERVICES_FIELD_AVAILABLE_SAT = "Available Sat";
export const SPA_SERVICES_FIELD_INACTIVE = "Inactive";
export const SPA_SERVICES_FIELD_LINKED_ITEM = "Linked Item";
export const SPA_SERVICES_FIELD_SERVICE_COMMENT = "Service Comment";
export const SPA_SERVICES_FIELD_PRICE_TYPE = "Price Type";
export const SPA_SERVICES_FIELD_SERVICE_EQUIPMENT = "Service Equipment";
export const SPA_SERVICES_FIELD_SERVICE_LOCATIONS = "Service Locations";

export const SPA_SERVICES_YES_NO_OPTIONS = YesOrNo;

export const SPA_SERVICES_YES = "Yes";
export const SPA_SERVICES_NO = "No";

export const SPA_SERVICES_DEFAULT_NO = SPA_SERVICES_NO;
export const SPA_SERVICES_DEFAULT_YES = SPA_SERVICES_YES;

export const SPA_SERVICES_SAMPLE_TEST_MARKER = "sample_test_agilysys";

export const SPA_SERVICES_SERVICE_GROUP_PLACEHOLDER = "Select service group";
export const SPA_SERVICES_PRICE_TYPE_PLACEHOLDER = "Select price type(s)";
export const SPA_SERVICES_SERVICE_EQUIPMENT_PLACEHOLDER =
  "Select service equipment";
export const SPA_SERVICES_SERVICE_LOCATIONS_PLACEHOLDER =
  "Select service location(s)";
export const SPA_SERVICES_LINKED_ITEM_PLACEHOLDER = "Select retail item";

export const SPA_SERVICES_VALIDATION_MESSAGES = {
  serviceGroupMandatory: "Service Group is mandatory",
  serviceCodeMandatory: "Service Code is mandatory",
  serviceDescriptionMandatory: "Service Description is mandatory",
  duplicateEntry: "Cannot have duplicate entries",
  priceMandatory: "Price is mandatory",
  serviceTimeMandatory: "Service Time is mandatory",
  linkedItemMandatory: "Linked retail item is mandatory",
} as const;

/** Rows with sample_test_agilysys in any string value are demo data. */
export const isSampleSpaServiceGroupRow = (obj: any): boolean =>
  Object.values(obj ?? {}).some((value: any) => {
    if (typeof value === "string") {
      return value.toLowerCase().includes(SPA_SERVICES_SAMPLE_TEST_MARKER);
    }
    if (Array.isArray(value)) {
      return value.some(
        (item) =>
          typeof item === "string" &&
          item.toLowerCase().includes(SPA_SERVICES_SAMPLE_TEST_MARKER),
      );
    }
    return false;
  });

export const stringContainsSpaSampleTestMarker = (
  s: string | null | undefined,
) =>
  typeof s === "string" &&
  s.toLowerCase().includes(SPA_SERVICES_SAMPLE_TEST_MARKER);

/** Maps a service-group autocomplete value (name or code) to the alphanumeric code for Excel import. */
export const resolveSpaServiceGroupCode = (
  serviceGroupValue: unknown,
  serviceGroups: unknown,
): string => {
  if (serviceGroupValue == null || String(serviceGroupValue).trim() === "") {
    return "";
  }

  const value = String(serviceGroupValue).trim();
  if (value.includes(" - ")) {
    return value.split(" - ")[0].trim();
  }

  const groups = Array.isArray(serviceGroups) ? serviceGroups : [];
  for (const group of groups) {
    if (!group || typeof group !== "object") {
      continue;
    }
    const record = group as Record<string, unknown>;
    const code = record[SPA_SERVICES_FIELD_SERVICE_GROUP_CODE];
    const name = record[SPA_SERVICES_FIELD_SERVICE_GROUP_NAME];
    if (code != null && String(code).trim() === value) {
      return String(code).trim();
    }
    if (name != null && String(name).trim() === value && code != null) {
      return String(code).trim();
    }
  }

  return value;
};

const toLinkedSelectionArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item != null && String(item).trim() !== "")
      .map((item) => String(item).trim());
  }
  if (value != null && String(value).trim() !== "") {
    return [String(value).trim()];
  }
  return [];
};

export const formatSpaLinkedMasterOption = (
  code: string,
  description: string,
): string => (description ? `${code} - ${description}` : code);

/** Maps a saved autocomplete value to the current master "CODE - Description" option. */
export const resolveSpaLinkedMasterSelection = (
  selected: unknown,
  masterRows: unknown,
  codeField: string,
  descriptionField: string,
): string | null => {
  if (selected == null || String(selected).trim() === "") {
    return null;
  }

  const trimmed = String(selected).trim();
  if (trimmed.toLowerCase().includes(SPA_SERVICES_SAMPLE_TEST_MARKER)) {
    return null;
  }

  const selectedCode = trimmed.includes(" - ")
    ? trimmed.slice(0, trimmed.indexOf(" - ")).trim()
    : trimmed;
  const selectedDescription = trimmed.includes(" - ")
    ? trimmed.slice(trimmed.indexOf(" - ") + 3).trim()
    : "";

  const masters = Array.isArray(masterRows) ? masterRows : [];
  let descriptionMatch: Record<string, unknown> | null = null;
  let descriptionMatchCount = 0;

  for (const row of masters) {
    if (!row || typeof row !== "object" || isSampleSpaServiceGroupRow(row)) {
      continue;
    }

    const record = row as Record<string, unknown>;
    const code = String(record[codeField] ?? "").trim();
    const description = String(record[descriptionField] ?? "").trim();
    if (!code) {
      continue;
    }

    const formatted = formatSpaLinkedMasterOption(code, description);

    if (trimmed === formatted || trimmed === code || selectedCode === code) {
      return formatted;
    }

    if (
      selectedDescription &&
      description &&
      selectedDescription === description
    ) {
      descriptionMatch = record;
      descriptionMatchCount += 1;
    }
  }

  if (descriptionMatchCount === 1 && descriptionMatch) {
    const code = String(descriptionMatch[codeField] ?? "").trim();
    const description = String(descriptionMatch[descriptionField] ?? "").trim();
    if (code) {
      return formatSpaLinkedMasterOption(code, description);
    }
  }

  return null;
};

/** Rebinds service price type / equipment selections to current master records. */
export const normalizeSpaServiceLinkedSelections = (
  service: Record<string, unknown>,
  priceTypes: unknown,
  serviceEquipment: unknown,
): void => {
  service[SPA_SERVICES_FIELD_PRICE_TYPE] = toLinkedSelectionArray(
    service[SPA_SERVICES_FIELD_PRICE_TYPE],
  )
    .map((value) =>
      resolveSpaLinkedMasterSelection(
        value,
        priceTypes,
        "Price Type Code",
        "Price Type Description",
      ),
    )
    .filter((value): value is string => Boolean(value));

  service[SPA_SERVICES_FIELD_SERVICE_EQUIPMENT] = toLinkedSelectionArray(
    service[SPA_SERVICES_FIELD_SERVICE_EQUIPMENT],
  )
    .map((value) =>
      resolveSpaLinkedMasterSelection(
        value,
        serviceEquipment,
        "Equipment Code",
        "Equipment Description",
      ),
    )
    .filter((value): value is string => Boolean(value));
};
